/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useState, useMemo } from "react";
import {
  Box,
  Stack,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import DetailRow from "../components/DetailRow";
import DasPowerDetectorChart from "./DasPowerDetectorChart";
import { 
  createPowerDetectorChartConfig, 
  buildPowerDetectorParams,
  PowerDetectorParams 
} from "./dasChartUtils";
import {
  formatPower,
  isValidPowerSignal,
  getSignalLedColor,
  isBitSet,
  getAlarmLedColor,
} from "../utils";

const RauDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const isDisconnected = statusLabel === "Disconnected";

  // Global alarms
  const rauAlarms = parsedAttributes.rauAlarms;
  const rauAlarmsMask = parsedAttributes.rauAlarmsMask;
  const hasGlobalAlarms = rauAlarms !== undefined || rauAlarmsMask !== undefined;

  // Global alarm definitions (RAU specific bits)
  const globalAlarmDefinitions: AlarmDefinition[] = [
    { bit: 5, name: "Over Temperature" },
    { bit: 6, name: "Antenna Disconnect" },
    { bit: 7, name: "Inconsistent Version" },
    { bit: 8, name: "Adjustment Fault" },
    { bit: 9, name: "HW Failure" },
    { bit: 11, name: "Power Supply" },
  ];

  // Band-specific alarm definitions (RAU has 3 alarms per band)
  const bandAlarmDefinitions: AlarmDefinition[] = [
    { bit: 0, name: "DL Out Pwr Low" },
    { bit: 1, name: "UL In Pwr High" },
    { bit: 2, name: "Service Off" },
  ];

  // Extract band names from the bands field (comma-separated string like "AWS1,700MHz,PCS,850MHz")
  const bandNames = parsedAttributes.bands 
    ? parsedAttributes.bands.split(',').map((b: string) => b.trim()) 
    : [];

  // Extract band alarm data with band names
  const bandAlarms: Array<{ bandNumber: number; bandName: string; alarms: any; mask: any }> = [];
  for (let i = 1; i <= 4; i++) {
    const alarms = parsedAttributes[`rauAlarmsBand${i}`];
    const mask = parsedAttributes[`rauAlarmsMaskBand${i}`];
    const bandName = bandNames[i - 1] || `Band ${i}`;
    if (alarms !== undefined || mask !== undefined) {
      bandAlarms.push({
        bandNumber: i,
        bandName,
        alarms,
        mask,
      });
    }
  }
  const hasBandAlarms = bandAlarms.length > 0;

  // Extract band data for bands 1-4
  const bands = [];
  for (let i = 1; i <= 4; i++) {
    const dlKey = `dlPowerBand${i}`;
    const ulKey = `ulPowerBand${i}`;
    const serviceStateKey = `rauServiceStateBand${i}`;
    const outPowerKey = `rauOutPowerBand${i}`;
    const ulLimiterKey = `rauUlLimiterBand${i}`;
    const extFilterKey = i <= 2 ? `rauExtFilterBand${i}` : null; // Only bands 1-2 have ext filters
    
    const dl = parsedAttributes[dlKey];
    const ul = parsedAttributes[ulKey];
    const serviceState = parsedAttributes[serviceStateKey];
    const outPower = parsedAttributes[outPowerKey];
    const ulLimiter = parsedAttributes[ulLimiterKey];
    const extFilter = extFilterKey ? parsedAttributes[extFilterKey] : undefined;
    
    if (dl !== undefined || ul !== undefined || serviceState !== undefined || outPower !== undefined || ulLimiter !== undefined || extFilter !== undefined) {
      bands.push({ 
        band: i, 
        name: bandNames[i - 1] || `Band ${i}`, // Use band name or fallback to "Band X"
        dl, 
        ul,
        serviceState,
        outPower,
        ulLimiter,
        extFilter
      });
    }
  }

  const hasBandInfo = bands.length > 0;

  // State for fullscreen chart
  const [fullscreenChart, setFullscreenChart] = useState<'dl' | 'ul' | null>(null);
  
  const handleOpenFullscreen = (chartType: 'dl' | 'ul') => {
    setFullscreenChart(chartType);
  };
  
  const handleCloseFullscreen = () => {
    setFullscreenChart(null);
  };

  // Helper function to format on/off values
  const formatOnOff = (value: any): string => {
    if (value === undefined || value === null) return "N/A";
    return value === 1 ? "On" : "Off";
  };

  // Build indicator lists and chart configs for power charts
  const bandNumbers = bands.map(b => b.band);
  const displayBandNames = bands.map(b => b.name);
  
  // RemoteEquipment object_key for chart queries
  const remoteEquipmentObjectKey = useMemo(() => {
    return device.objectKey ||
      parsedAttributes.objectKey ||
      parsedAttributes.manageObject ||
      parsedAttributes.managedObject ||
      parsedAttributes.objectId ||
      'RAU-1';
  }, [
    JSON.stringify([
      device.objectKey,
      parsedAttributes.objectKey,
      parsedAttributes.manageObject,
      parsedAttributes.managedObject,
      parsedAttributes.objectId
    ])
  ]);

  // Build indicators
  const buildBandIndicators = (prefix: string, numbers: number[]) => {
    return numbers.map(n => `${prefix}${n}`);
  };

  const ulIndicators = useMemo(() => buildBandIndicators('ulPowerBand', bandNumbers), [bandNumbers]);
  const dlIndicators = useMemo(() => buildBandIndicators('dlPowerBand', bandNumbers), [bandNumbers]);

  // Chart parameters
  const ulParams = useMemo<PowerDetectorParams>(() => ({
    remoteEquipmentObjectKey,
    indicators: ulIndicators,
    bandNames: displayBandNames,
    bandNumbers,
    yAxisMin: -150,  // RAU UL min power
    yAxisMax: -20,   // RAU UL max power
  }), [remoteEquipmentObjectKey, ulIndicators, displayBandNames, bandNumbers]);

  const dlParams = useMemo<PowerDetectorParams>(() => ({
    remoteEquipmentObjectKey,
    indicators: dlIndicators,
    bandNames: displayBandNames,
    bandNumbers,
    yAxisMin: -10,   // RAU DL min power
    yAxisMax: 80,    // RAU DL max power
  }), [remoteEquipmentObjectKey, dlIndicators, displayBandNames, bandNumbers]);

  // Memoize chart configs
  const ulChartConfig = useMemo(() => 
    hasBandInfo ? createPowerDetectorChartConfig('ul', ulParams) : null,
    [hasBandInfo, ulParams]
  );

  const dlChartConfig = useMemo(() => 
    hasBandInfo ? createPowerDetectorChartConfig('dl', dlParams) : null,
    [hasBandInfo, dlParams]
  );

  const siteId = device.servicesDeviceObjectKey || device.serialNumber || '';

  const ulChartAdditionalParams = useMemo(() => 
    hasBandInfo ? {
      ...buildPowerDetectorParams(siteId, remoteEquipmentObjectKey, ulIndicators)
    } : null,
    [hasBandInfo, siteId, remoteEquipmentObjectKey, ulIndicators]
  );

  const dlChartAdditionalParams = useMemo(() => 
    hasBandInfo ? {
      ...buildPowerDetectorParams(siteId, remoteEquipmentObjectKey, dlIndicators)
    } : null,
    [hasBandInfo, siteId, remoteEquipmentObjectKey, dlIndicators]
  );

  // If alarmsOnly mode, only render alarm sections
  if (alarmsOnly) {
    if (!hasGlobalAlarms && !hasBandAlarms) return null;
    
    return (
      <>
        {/* Global Alarms Section */}
        {hasGlobalAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={rauAlarms}
                alarmsMask={rauAlarmsMask}
                alarmDefinitions={globalAlarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}

        {/* Band Alarms Section */}
        {hasBandAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Band Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Alarm</TableHead>
                    {bandAlarms.map((band) => (
                      <TableHead key={band.bandNumber} className="text-center min-w-[120px]">
                        {band.bandName}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bandAlarmDefinitions.map((alarmDef) => (
                    <TableRow key={alarmDef.bit}>
                      <TableCell>{alarmDef.name}</TableCell>
                      {bandAlarms.map((band) => {
                        const isAlarmActive = isBitSet(band.alarms, alarmDef.bit);
                        const isMaskEnabled = isBitSet(band.mask, alarmDef.bit);
                        return (
                          <TableCell key={`${band.bandNumber}-${alarmDef.bit}`} className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <div
                                className="w-2.5 h-2.5 rounded-full"
                                style={{ backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive) }}
                              />
                              <Checkbox
                                checked={isMaskEnabled}
                                disabled
                                size="small"
                                sx={{
                                  padding: 0,
                                  color: "var(--foreground)",
                                  "&.Mui-disabled": { color: "var(--muted-foreground)" },
                                }}
                              />
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  // Normal mode: show RF Information only, no alarms
  if (!hasBandInfo) return null;

  return (
    <>
      <Card className="mb-2 overflow-hidden">
        <CardHeader className="pb-3 px-4 pt-4">
          <CardTitle className="text-sm">RF Information</CardTitle>
        </CardHeader>
        {hasBandInfo && (
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[150px]">Parameter</TableHead>
                {bands.map(({ band, name }) => (
                  <TableHead key={band} className="min-w-[120px]">
                    {name}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-semibold text-muted-foreground">Service State</TableCell>
                {bands.map(({ band, serviceState }) => (
                  <TableCell key={`serviceState-${band}`}>
                    {serviceState !== undefined ? (serviceState === 1 ? "On" : "Off") : "N/A"}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell className="font-semibold text-muted-foreground">UL Limiter</TableCell>
                {bands.map(({ band, ulLimiter }) => (
                  <TableCell key={`ulLimiter-${band}`}>
                    {ulLimiter !== undefined ? (ulLimiter === 1 ? "On" : "Off") : "N/A"}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell className="font-semibold text-muted-foreground">Ext. Filters</TableCell>
                {bands.map(({ band, extFilter }) => (
                  <TableCell key={`extFilter-${band}`}>
                    {extFilter !== undefined ? (extFilter === 1 ? "On" : "Off") : "N/A"}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell className="font-semibold text-muted-foreground">Out Power</TableCell>
                {bands.map(({ band, outPower }) => (
                  <TableCell key={`outPower-${band}`}>{formatPower(outPower)}</TableCell>
                ))}
              </TableRow>
              {/* Embedded DL Power Chart */}
              <TableRow className="hover:bg-transparent border-b-0">
                <TableCell colSpan={bands.length + 1} className="px-3 pt-3 pb-0 border-b-0">
                  <div style={{ height: 300, width: '100%' }}>
                    {hasBandInfo && dlChartConfig && dlChartAdditionalParams && (
                      <DasPowerDetectorChart
                        config={dlChartConfig}
                        onFullscreenToggle={() => handleOpenFullscreen('dl')}
                        isFullscreen={false}
                        additionalParams={dlChartAdditionalParams}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
              <TableRow className="hover:bg-transparent border-b-0">
                <TableCell colSpan={bands.length + 1} className="p-3 pt-0 align-top">
                  <Card>
                    <CardContent className="p-0">
                      <div className="flex divide-x divide-border">
                        {bands.map((band) => (
                          <div key={band.band} className="flex-1 px-3 py-2">
                            <p className="text-xs text-muted-foreground mb-1">{band.name}</p>
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getSignalLedColor(isValidPowerSignal(band.dl)) }} />
                              <span className="text-sm font-semibold text-foreground">{formatPower(band.dl)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TableCell>
              </TableRow>
              {/* Embedded UL Power Chart */}
              <TableRow className="hover:bg-transparent border-b-0">
                <TableCell colSpan={bands.length + 1} className="px-3 pt-3 pb-0 border-b-0">
                  <div style={{ height: 300, width: '100%' }}>
                    {hasBandInfo && ulChartConfig && ulChartAdditionalParams && (
                      <DasPowerDetectorChart
                        config={ulChartConfig}
                        onFullscreenToggle={() => handleOpenFullscreen('ul')}
                        isFullscreen={false}
                        additionalParams={ulChartAdditionalParams}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={bands.length + 1} className="p-3 pt-0 align-top">
                  <Card>
                    <CardContent className="p-0">
                      <div className="flex divide-x divide-border">
                        {bands.map((band) => (
                          <div key={band.band} className="flex-1 px-3 py-2">
                            <p className="text-xs text-muted-foreground mb-1">{band.name}</p>
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getSignalLedColor(isValidPowerSignal(band.ul)) }} />
                              <span className="text-sm font-semibold text-foreground">{formatPower(band.ul)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      )}
      </Card>

      {/* Global Alarms Section */}
      {hasGlobalAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={rauAlarms}
              alarmsMask={rauAlarmsMask}
              alarmDefinitions={globalAlarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}

      {/* Band Alarms Section */}
      {hasBandAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Band Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Alarm</TableHead>
                  {bandAlarms.map((band) => (
                    <TableHead key={band.bandNumber} className="text-center min-w-[120px]">
                      {band.bandName}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {bandAlarmDefinitions.map((alarmDef) => (
                  <TableRow key={alarmDef.bit}>
                    <TableCell>{alarmDef.name}</TableCell>
                    {bandAlarms.map((band) => {
                      const isAlarmActive = isBitSet(band.alarms, alarmDef.bit);
                      const isMaskEnabled = isBitSet(band.mask, alarmDef.bit);
                      return (
                        <TableCell key={`${band.bandNumber}-${alarmDef.bit}`} className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <div
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive) }}
                            />
                            <Checkbox
                              checked={isMaskEnabled}
                              disabled
                              size="small"
                              sx={{
                                padding: 0,
                                color: "var(--foreground)",
                                "&.Mui-disabled": { color: "var(--muted-foreground)" },
                              }}
                            />
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* DL Power Fullscreen Dialog */}
      <Dialog
        open={fullscreenChart === 'dl'}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: '1px solid var(--border)',
            borderRadius: '12px',
            minHeight: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          pb: 2,
          px: 4
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--foreground)' }}>
            DL Input Power
          </Typography>
          <IconButton onClick={handleCloseFullscreen} size="small" sx={{ color: 'var(--foreground)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {fullscreenChart === 'dl' && hasBandInfo && dlChartConfig && dlChartAdditionalParams && (
            <DasPowerDetectorChart
              config={dlChartConfig}
              onFullscreenToggle={handleCloseFullscreen}
              isFullscreen={true}
              additionalParams={dlChartAdditionalParams}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* UL Power Fullscreen Dialog */}
      <Dialog
        open={fullscreenChart === 'ul'}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: '1px solid var(--border)',
            borderRadius: '12px',
            minHeight: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          pb: 2,
          px: 4
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--foreground)' }}>
            UL Input Power
          </Typography>
          <IconButton onClick={handleCloseFullscreen} size="small" sx={{ color: 'var(--foreground)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {fullscreenChart === 'ul' && hasBandInfo && ulChartConfig && ulChartAdditionalParams && (
            <DasPowerDetectorChart
              config={ulChartConfig}
              onFullscreenToggle={handleCloseFullscreen}
              isFullscreen={true}
              additionalParams={ulChartAdditionalParams}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RauDeviceDetails;
